import {XMarkIcon} from "@heroicons/react/24/outline";
import {
    AnimatePresence,
    animate,
    motion,
    useIsPresent,
    useMotionValue,
    useTransform,
} from "motion/react";
import * as React from "react";
import ReactMarkdown from "react-markdown";

function GradientOverlay(props: {size: {width: number; height: number}}) {
    const {size} = props;
    const breathe = useMotionValue(0);
    const isPresent = useIsPresent();

    React.useEffect(() => {
        if (!isPresent) {
            animate(breathe, 0, {duration: 0.5, ease: "easeInOut"});
            return;
        }

        async function playBreathingAnimation() {
            await animate(breathe, 1, {
                duration: 0.5,
                delay: 0.35,
                ease: [0, 0.55, 0.45, 1],
            });

            animate(breathe, [null, 0.7, 1], {
                duration: 15,
                repeat: Infinity,
                repeatType: "loop",
                ease: "easeInOut",
            });
        }

        void playBreathingAnimation();
    }, [isPresent, breathe]);

    const enterDuration = 0.75;
    const exitDuration = 0.5;
    const expandingCircleRadius = size.width / 3;

    const topLeftScale = useTransform(breathe, (v) => v);
    const bottomRightScale = useTransform(breathe, (v) => v);

    return (
        <div className="pointer-events-none absolute inset-0 z-40 overflow-hidden">
            <motion.div
                className="absolute rounded-full bg-indigo-300/80"
                style={{
                    left: `calc(50% - ${expandingCircleRadius / 2}px)`,
                    top: "100%",
                    width: expandingCircleRadius,
                    height: expandingCircleRadius,
                    originX: 0.5,
                    originY: 1,
                    filter: "blur(15px)",
                }}
                initial={{
                    scale: 0,
                    opacity: 1,
                    backgroundColor: "rgb(165, 180, 252)",
                }}
                animate={{
                    scale: 10,
                    opacity: 0.2,
                    backgroundColor: "rgb(99, 102, 241)",
                    transition: {
                        duration: enterDuration,
                        opacity: {duration: enterDuration, ease: "easeInOut"},
                    },
                }}
                exit={{
                    scale: 0,
                    opacity: 1,
                    backgroundColor: "rgb(165, 180, 252)",
                    transition: {duration: exitDuration},
                }}
            />

            <motion.div
                className="absolute rounded-full bg-indigo-600/90"
                style={{
                    scale: topLeftScale,
                    width: size.width * 2,
                    height: size.width * 2,
                    top: -size.width,
                    left: -size.width,
                    filter: "blur(100px)",
                }}
                initial={{opacity: 0}}
                animate={{
                    opacity: 0.9,
                    transition: {duration: enterDuration},
                }}
                exit={{
                    opacity: 0,
                    transition: {duration: exitDuration},
                }}
            />

            <motion.div
                className="absolute rounded-full bg-indigo-500/90"
                style={{
                    scale: bottomRightScale,
                    width: size.width * 2,
                    height: size.width * 2,
                    top: size.height - size.width,
                    left: 0,
                    filter: "blur(100px)",
                }}
                initial={{opacity: 0}}
                animate={{
                    opacity: 0.9,
                    transition: {duration: enterDuration},
                }}
                exit={{
                    opacity: 0,
                    transition: {duration: exitDuration},
                }}
            />
        </div>
    );
}

function CalculationOverlay(props: {content: string; onClose: () => void}) {
    const {onClose} = props;
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [size, setSize] = React.useState({width: 0, height: 0});

    React.useEffect(() => {
        setSize({
            width: containerRef.current?.clientWidth ?? 0,
            height: containerRef.current?.clientHeight ?? 0,
        });
    }, []);

    const transition = {
        duration: 0.35,
        ease: [0.59, 0, 0.35, 1] as const,
    };

    const enteringState = {
        rotateX: 0,
        skewY: 0,
        scaleY: 1,
        scaleX: 1,
        y: 0,
        transition: {
            ...transition,
            y: {type: "spring" as const, visualDuration: 0.7, bounce: 0.2},
        },
    };

    const exitingState = {
        rotateX: -5,
        skewY: -1.5,
        scaleY: 2,
        scaleX: 0.4,
        y: 100,
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="How this is calculated"
            ref={containerRef}
            className="fixed inset-0 z-50 overflow-hidden"
            onClick={onClose}
        >
            <GradientOverlay size={size} />

            <motion.div
                className="scrollbar-hidden absolute inset-0 z-50 flex overflow-y-auto bg-indigo-800/60 backdrop-blur-sm"
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                exit={{opacity: 0}}
                transition={transition}
            >
                <motion.div
                    className="mx-auto max-w-2xl"
                    onClick={(e) => e.stopPropagation()}
                    initial={exitingState}
                    animate={enteringState}
                    exit={exitingState}
                    transition={transition}
                    style={{
                        transformPerspective: 1000,
                        originX: 0.5,
                        originY: 0,
                    }}
                >
                    <div className="sticky top-0 flex justify-end px-4 pt-4">
                        <button
                            type="button"
                            aria-label="Close"
                            onClick={onClose}
                            className="text-white/90 hover:text-white"
                        >
                            <XMarkIcon className="size-9" />
                        </button>
                    </div>
                    <ReactMarkdown className="-mt-9 space-y-3 px-4 pb-4 text-center text-base text-pretty text-white/90 [&_h2]:text-center [&_h2]:text-3xl [&_h2]:font-light [&_h3]:text-center [&_h3]:text-2xl [&_h3]:font-light [&_strong]:font-medium [&_ul]:mx-auto [&_ul]:max-w-9/10 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-4 [&_ul]:text-left [&_ul]:text-white/90">
                        {props.content}
                    </ReactMarkdown>
                </motion.div>
            </motion.div>
        </div>
    );
}

export function CalculationModal(props: {
    open: boolean;
    content: string;
    onClose: () => void;
}) {
    React.useEffect(() => {
        if (!props.open) return;
        const close = (event: KeyboardEvent) => {
            if (event.key === "Escape") props.onClose();
        };
        document.addEventListener("keydown", close);
        return () => document.removeEventListener("keydown", close);
    }, [props.open, props.onClose]);
    return (
        <AnimatePresence>
            {props.open && (
                <CalculationOverlay content={props.content} onClose={props.onClose} />
            )}
        </AnimatePresence>
    );
}
